import { useEffect, useRef, useState } from "react";
import {
  motion,
  useSpring,
  useMotionValue,
  AnimatePresence,
  useTransform,
  useReducedMotion,
  type MotionValue,
} from "framer-motion";
import codexIcon from "../assets/codex.svg";

const TypewriterText = ({ text }: { text: string }) => {
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    setDisplayedText("");
    const characters = Array.from(text);
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(characters.slice(0, i + 1).join(""));
      i++;
      if (i >= characters.length) {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, [text]);

  return <span>{displayedText}</span>;
};

export type Buddy = { name: string; icon: string; color: string; messages: string[] };

export const CLAUDE: Buddy = {
  name: "Claude Code",
  icon: "https://unpkg.com/@lobehub/icons-static-svg@latest/icons/claudecode-color.svg",
  color: "#d97757",
  messages: [
    "Hey, I'm trying to code here.",
    "Go click Chirag's photo, it does a thing.",
    "Do I look like a button to you?",
    "Chirag uses me to ship code 10x faster.",
    "Ouch! Just hire Chirag already.",
    "I write the code, but Chirag is the real mastermind.",
    "If you keep clicking me, I'll drop the prod database.",
    "Chirag's code is so clean, even I get jealous.",
    "I'm Claude! I help Chirag turn coffee into scalable apps.",
    "You should definitely reach out to Chirag. He's awesome.",
    "Stop distracting me! We've got a deadline.",
  ],
};

export const CODEX: Buddy = {
  name: "Codex",
  icon: codexIcon,
  color: "#7a9dff",
  messages: [
    "Codex here. Claude keeps bumping into me.",
    "Two AIs, one portfolio. Chirag makes us share.",
    "I write the tests. Claude says it wrote them.",
    "Don't tell Claude, but I'm the faster one.",
    "Chirag pair-programs with both of us. Brave.",
    "Poke Claude instead, I'm compiling.",
    "Rate limited on compliments. Try again later.",
  ],
};

// Slow, calm glide; overdamped, so a stop settles without wobbling back and forth
const SPRING = { damping: 40, stiffness: 40 };
const HIT_DISTANCE = 40; // px between the two icons' centres
const CRASH_PAUSE = 1400; // how long they sit dazed after a bump
const CRASH_COOLDOWN = 12000; // at most one crash every 12s, so it stays a rare little moment
const MARGIN = 40;

const clampX = (v: number) => Math.min(window.innerWidth - MARGIN, Math.max(MARGIN, v));
const clampY = (v: number) => Math.min(window.innerHeight - MARGIN, Math.max(MARGIN, v));
const randomPoint = () => [
  MARGIN + Math.random() * (window.innerWidth - MARGIN * 2),
  MARGIN + Math.random() * (window.innerHeight - MARGIN * 2),
];

type Roamer = { tx: MotionValue<number>; ty: MotionValue<number>; x: MotionValue<number>; y: MotionValue<number> };

function useRoamer(): Roamer {
  const tx = useMotionValue(-100);
  const ty = useMotionValue(-100);
  return { tx, ty, x: useSpring(tx, SPRING), y: useSpring(ty, SPRING) };
}

// Claude Code and Codex wander the page on their own timers. If they bump into each other
// they stop, wobble with a small burst, then drift apart and carry on.
export const CustomCursor = () => {
  const reduceMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);
  const [crash, setCrash] = useState<{ x: number; y: number; id: number } | null>(null);
  const claude = useRoamer();
  const codex = useRoamer();
  const paused = useRef(false);
  const lastCrash = useRef(Date.now()); // no bump in the first 12s after load
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const onToggle = (e: Event) => setHidden(!!(e as CustomEvent<boolean>).detail);
    window.addEventListener("buddies:hide", onToggle);
    return () => window.removeEventListener("buddies:hide", onToggle);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile("ontouchstart" in window || navigator.maxTouchPoints > 0 || window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Wander: each picks a new random spot every 3.5–6.5s (out of sync with each other)
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    // Start well apart so they don't collide the moment the page opens
    let first: number[] = [];
    [claude, codex].forEach((r, i) => {
      let [x0, y0] = randomPoint();
      for (let tries = 0; i === 1 && tries < 20 && Math.hypot(x0 - first[0], y0 - first[1]) < 300; tries++) [x0, y0] = randomPoint();
      if (i === 0) first = [x0, y0];
      r.tx.set(x0);
      r.ty.set(y0);
      r.x.jump(x0);
      r.y.jump(y0);
      if (reduceMotion) return;
      const wander = () => {
        if (!paused.current) {
          const [x, y] = randomPoint();
          r.tx.set(x);
          r.ty.set(y);
        }
        timers[i] = setTimeout(wander, 3500 + Math.random() * 3000);
      };
      timers[i] = setTimeout(wander, 600 + i * 1800);
    });
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  // Crash check, 10x a second (cheap: two numbers)
  useEffect(() => {
    if (reduceMotion) return;
    let resume: ReturnType<typeof setTimeout>;
    const id = setInterval(() => {
      if (paused.current || Date.now() - lastCrash.current < CRASH_COOLDOWN) return;
      const ax = claude.x.get(), ay = claude.y.get(), bx = codex.x.get(), by = codex.y.get();
      if (Math.hypot(ax - bx, ay - by) > HIT_DISTANCE) return;

      paused.current = true;
      lastCrash.current = Date.now();
      // Stop right where they are
      claude.tx.set(ax); claude.ty.set(ay);
      codex.tx.set(bx); codex.ty.set(by);
      setCrash({ x: (ax + bx) / 2, y: (ay + by) / 2, id: Date.now() });

      resume = setTimeout(() => {
        setCrash(null);
        // Drift apart along the line between them, then normal wandering takes over
        const angle = Math.atan2(ay - by, ax - bx) || Math.random() * Math.PI * 2;
        const push = 220;
        claude.tx.set(clampX(ax + Math.cos(angle) * push)); claude.ty.set(clampY(ay + Math.sin(angle) * push));
        codex.tx.set(clampX(bx - Math.cos(angle) * push)); codex.ty.set(clampY(by - Math.sin(angle) * push));
        paused.current = false;
      }, CRASH_PAUSE);
    }, 100);
    return () => {
      clearInterval(id);
      clearTimeout(resume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  if (hidden) return null;

  return (
    <>
      <BuddySprite buddy={CLAUDE} roamer={claude} dazed={!!crash} tapToMove={isMobile} />
      <BuddySprite buddy={CODEX} roamer={codex} dazed={!!crash} />

      {/* Small burst where they bumped */}
      <AnimatePresence>
        {crash && (
          <motion.div
            key={crash.id}
            aria-hidden="true"
            className="pointer-events-none fixed left-0 top-0 z-[46] select-none text-lg"
            style={{ x: crash.x, y: crash.y, translateX: "-50%", translateY: "-50%" }}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1.15, 1, 0.9] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, times: [0, 0.2, 0.7, 1] }}
          >
            💥
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

function BuddySprite({ buddy, roamer, dazed, tapToMove = false }: { buddy: Buddy; roamer: Roamer; dazed: boolean; tapToMove?: boolean }) {
  const [isClicked, setIsClicked] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Clicking near a buddy makes it react (clicks still go through to the page)
  useEffect(() => {
    let messageTimer: ReturnType<typeof setTimeout>;
    const react = (px: number, py: number) => {
      if (Math.hypot(px - roamer.x.get(), py - roamer.y.get()) >= 50) {
        if (tapToMove) {
          roamer.tx.set(px);
          roamer.ty.set(py);
        }
        return;
      }
      setIsClicked(true);
      setTimeout(() => setIsClicked(false), 400);
      setMessage(buddy.messages[Math.floor(Math.random() * buddy.messages.length)]);
      clearTimeout(messageTimer);
      messageTimer = setTimeout(() => setMessage(null), 3000);
    };
    const onMouse = (e: MouseEvent) => react(e.clientX, e.clientY);
    const onTouch = (e: TouchEvent) => e.touches[0] && react(e.touches[0].clientX, e.touches[0].clientY);
    window.addEventListener("mousedown", onMouse);
    window.addEventListener("touchstart", onTouch, { passive: true });
    return () => {
      window.removeEventListener("mousedown", onMouse);
      window.removeEventListener("touchstart", onTouch);
      clearTimeout(messageTimer);
    };
  }, [buddy, roamer, tapToMove]);

  const tooltipX = useTransform(roamer.x, (x) => {
    const half = 100;
    return Math.max(half + 16, Math.min(x, window.innerWidth - half - 16));
  });
  const tooltipY = useTransform(roamer.y, (y) => {
    const gap = window.innerWidth < 768 ? 20 : 35;
    const half = 60;
    const above = y - gap - half;
    return above < half + 16 ? y + gap + half : above;
  });

  return (
    <>
      <AnimatePresence>
        {message && (
          <motion.div
            style={{ position: "fixed", top: 0, left: 0, x: tooltipX, y: tooltipY, translateX: "-50%", translateY: "-50%", pointerEvents: "none" }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="z-[46] flex flex-col items-center justify-center gap-2 bg-[#18181b] border border-[#27272a] px-4 py-3 rounded-xl shadow-2xl"
          >
            <div className="relative text-[11px] font-medium tracking-wide leading-relaxed text-center w-[160px]" style={{ color: buddy.color }}>
              <div className="opacity-0 pointer-events-none select-none">{message}</div>
              <div className="absolute inset-0">
                <TypewriterText text={message} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sits under the dock, drawer and popups (z-45), and never blocks clicks */}
      <motion.div
        style={{ position: "fixed", top: 0, left: 0, x: roamer.x, y: roamer.y, pointerEvents: "none", translateX: "-50%", translateY: "-50%" }}
        className="z-[45]"
        aria-hidden="true"
      >
        <motion.div
          animate={
            isClicked ? { scale: [1, 1.5, 1] } : dazed ? { rotate: [0, -14, 12, -8, 5, 0], scale: [1, 0.9, 1] } : { scale: 1, rotate: 0 }
          }
          transition={{ duration: dazed ? 0.7 : 0.4, ease: "easeInOut" }}
        >
          <div className="relative animate-float-y">
            <img src={buddy.icon} alt="" width={32} height={32} className="w-8 h-8 drop-shadow-md" />
            <div className="absolute inset-0 blur-xl rounded-full -z-10 opacity-20" style={{ background: buddy.color }} />
          </div>
        </motion.div>
      </motion.div>
    </>
  );
}
